"use client";

import {JSX, useState} from "react";

import {LocationSearchView} from "@/features/locations";
import {GuestsView} from "@/features/rental/guests";
import {Payload, SimpleSearch, type SimpleSearchInstance} from "@/features/rental/simple-search/simple-search.model";

import {useMatchMedia, useRouterBridge} from "@/shared/hooks";
import {useServerRouter} from "@/shared/hooks/navigation/use-server-router";
import {useModel} from "@/shared/hooks/state-management/use-model";
import {useTranslation} from "@/shared/i18n";
import {ButtonBase, ButtonWithImage} from "@/shared/ui/buttons";
import {DateRangePickerField} from "@/shared/ui/form/datepickers";
import {DateRangeFilterTrigger} from "@/shared/ui/form/datepickers/range/parts";
import {Drawer} from "@/shared/ui/overlay/drawer";
import {getStaticPathIcon} from "@/shared/utils/get-static-path-icon";

import "@/features/rental/simple-search/ui/styles/single-search-view.scss";

interface SimpleSearchViewProps {
    className?: string;
    params: Payload;
}

export function SimpleSearchViewSingle({className, params}: SimpleSearchViewProps): JSX.Element {
    const [search, snapshot] = useModel<SimpleSearchInstance, [Payload?]>(SimpleSearch, params);
    const isMobile = useMatchMedia({maxWidth: 767});
    const [isExpanded, setIsExpanded] = useState(false);
    const [isDrawerOpen, setDrawerOpen] = useState(false);
    const {t} = useTranslation("search");
    useRouterBridge(search, {skipInitialSync: true});
    useServerRouter(search);
    const handleLocationClick = (): void => {
        if (isMobile) {
            setIsExpanded(true);
            setDrawerOpen(true);
        } else {
            setIsExpanded(true);
        }
    };

    const handleCloseDrawer = (): void => {
        setIsExpanded(false);
        setDrawerOpen(false);
    };

    const handlerClearGust = (): void => {
        search.guests.reset();
    };

    const handleSubmit = (): void => {
        search.submit();
    };

    const renderSearchFields = (): JSX.Element => (
        <div className="single-search__wrap">
            <div className="single-search__fields">
                <div className="single-search__field single-search__field-destination" onClick={handleLocationClick}>
                    <LocationSearchView
                        dropdownOffset={3}
                        model={search.locationSearch}
                        placeholder={t("locationPlaceholderLong")}
                        className="single-search__location"
                    />
                </div>

                <DateRangePickerField
                    model={search.dateRange}
                    startPlaceholder={t("checkIn")}
                    endPlaceholder={t("checkOut")}
                    dropdownZIndex={15}
                    dropdownOffset={6}
                    showClearButton={false}
                    singleCalendar={true}
                    renderTrigger={(props) => <DateRangeFilterTrigger {...props} />}
                    className="single-search__field single-search__field-dates"
                />

                <GuestsView dropdownOffset={5} model={search.guests} className="single-search__field single-search__field-guests">
                    {isMobile && (
                        <>
                            <ButtonBase variant="secondary" onClick={handlerClearGust} size="sm">
                                {t("$.common.clear")}
                            </ButtonBase>

                            <ButtonBase variant="primary" onClick={handleSubmit} size="sm">
                                {t("searchButton")}
                            </ButtonBase>
                        </>
                    )}
                </GuestsView>
            </div>
            <ButtonWithImage width={32} height={32} onClick={handleSubmit} variant="primary" icon={getStaticPathIcon("search")} />
        </div>
    );

    const renderMobileTrigger = (): JSX.Element => (
        <div className="single-search__wrap">
            <div className="single-search__fields">
                <div className="single-search__field single-search__field-fake" onClick={handleLocationClick}>
                    <div className="single-search__fake-input">
                        <span className="single-search__fake-input-text">
                            {snapshot.locationSearch.selectedOption?.name || t("locationPlaceholderLong")}
                        </span>
                    </div>
                </div>
            </div>
            <ButtonWithImage width={32} height={32} onClick={handleSubmit} variant="primary" icon={getStaticPathIcon("search")} />
        </div>
    );

    return (
        <>
            {!isMobile && (
                <div className={`single-search ${className || ""} ${isExpanded ? "single-search--expanded" : ""}`}>{renderSearchFields()}</div>
            )}

            {isMobile && <div className={`single-search ${className || ""}`}>{renderMobileTrigger()}</div>}

            {isMobile && (
                <Drawer open={isDrawerOpen} onClose={handleCloseDrawer} zIndex={15} direction="top" size="auto" offsetTop="68px" allowOverflow={true}>
                    <div className={`single-search ${className || ""} ${isExpanded ? "single-search--expanded" : ""}`}>{renderSearchFields()}</div>
                </Drawer>
            )}
        </>
    );
}
